import React from "react";
import { useGetPortfolioFeeDetails } from "api/holdings/useGetPortfolioFeeDetails";
import { CryptoData } from "api/minmax/useFetchMinMax";
import Collapsible from "components/Collapsible";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { OrderType } from "hooks/useTradingForm";

interface TradingFeesProps {
  portfolioId: string;
  portfolioCurrency: string;
  amount: string;
  limitOrderPrice: string;
  orderType: OrderType;
  unitPrice: number;
  tradeType: "units" | "amount";
}

export const useTradingFees = ({
  portfolioId,
  portfolioCurrency,
  amount,
  limitOrderPrice,
  orderType,
  unitPrice,
  tradeType,
}: TradingFeesProps) => {
  const { data: { feePercentage = 0 } = {} } =
    useGetPortfolioFeeDetails(portfolioId) || {};

  const minFee = portfolioCurrency === "SEK" ? 10 : 1;

  const unitsFee = Math.max(
    Number(orderType === "Limit" ? limitOrderPrice : unitPrice) *
      Number(amount) *
      Number(feePercentage),
    minFee
  ).toFixed(2);

  const amountFee = Math.max(
    Number(amount) * Number(feePercentage),
    minFee
  ).toFixed(2);

  const tentativeFee = tradeType === "units" ? unitsFee : amountFee;

  return { tentativeFee, feePercentage };
};

interface TradingSummaryProps {
  amount: string;
  limitOrderPrice: string;
  orderType: OrderType;
  tradeType: "units" | "amount";
  portfolioCurrency: string;
  securityCode: string;
  unitPrice: number;
  minMaxData: CryptoData;
  securityName: string;
  portfolioId: string;
  unitPriceWithSlippage: string;
  actionType: "buy" | "sell";
  totalToShow: string | number;
}

const TradingSummary: React.FC<TradingSummaryProps> = ({
  amount,
  limitOrderPrice,
  orderType,
  tradeType,
  portfolioCurrency,
  securityCode,
  unitPrice,
  minMaxData,
  securityName,
  portfolioId,
  unitPriceWithSlippage,
  actionType,
  totalToShow,
}) => {
  const { t } = useModifiedTranslation();

  const { tentativeFee, feePercentage } = useTradingFees({
    portfolioId,
    portfolioCurrency,
    amount,
    limitOrderPrice,
    orderType,
    unitPrice,
    tradeType,
  });

  const summaryHeaderRight = (
    <span className="text-white">
      {totalToShow} {tradeType === "units" ? portfolioCurrency : securityCode}
    </span>
  );

  return (
    <Collapsible
      title={<div className="text-base">{t("tradingWidget.summary")}</div>}
      headerRight={summaryHeaderRight}
      className="py-2 text-sm"
      contentClassName="mt-2"
      defaultOpen={false}
    >
      <div className="flex flex-col text-gray-500">
        <div className="flex justify-between items-center">
          <div>{t("tradingModal.feePercentage")}</div>
          <div>
            <span className="text-white">
              {(feePercentage * 100).toFixed(1)}
            </span>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div>{t("tradingModal.indicativeFee")}</div>
          <div>
            <span className="text-white">{tentativeFee}</span>{" "}
            {portfolioCurrency}
          </div>
        </div>
        {/* <div className="flex justify-between items-center">
          <div className="">{t("tradingModal.unitPrice")}</div>
          <div>
            <span className="text-white">
              {unitPrice.toFixed(getDecimalPlaces(portfolioCurrency))}
            </span>{" "}
            {portfolioCurrency}
          </div>
        </div> */}
        <div className="flex justify-between items-center">
          <div className="">
            {tradeType === "units"
              ? t("tradingModal.tradeAmount")
              : t("tradingModal.unitsInputLabel")}
          </div>
          <div>
            <span className="text-white">{totalToShow} </span>
            {tradeType === "units" ? portfolioCurrency : securityCode}
          </div>
        </div>
        <div className="mt-2 text-xs text-gray-500">
          {t("tradingModal.buyDisclaimer")}
        </div>
      </div>
    </Collapsible>
  );
};

export default TradingSummary;
